import { watch, ref, reactive, computed, nextTick, toRaw } from 'vue';
import { defineStore } from 'pinia';
import { useLocalStorage, StorageSerializers } from '@vueuse/core';
import { useRoute } from 'vue-router';

import { differenceInHours, formatISO, parseISO } from 'date-fns';
import { isObject } from 'lodash';

import { useApi } from '@/composables/useApi';

import useEditorChanges from '@/stores/editorChanges';
import useSupportStore from '@/stores/support';
/**
 * @typedef EditorStoreType
 * @type { actions | getters | state | import('pinia').Store }
 */
/**
 * @typedef useEditorStore
 * @type function
 * @returns EditorStoreType
 */

export default defineStore('editor', () => {
	const supportStore = useSupportStore();
	const VueRoute = useRoute();
	// local editor state
	const initializing = ref(true);
	const savingChanges = ref(false);
	const abortFunctions = reactive({
		config: {},
		overrides: {},
	});

	// current state pulled from api
	const usingDomainContent = ref(false);
	const overrides = ref([]);
	const productType = ref(null);
	const pages = ref({});
	const editors = ref([]);

	const progress = reactive({
		config: 0,
		overrides: 0,
	});
	const core = ref({});
	const articleBlacklist = ref([]);

	const pendingOverrides = computed(() =>
		overrides.value.filter(
			override => override.status === 'pending' && override.requires_review_from !== 'agent'
		)
	);
	const suggestions = computed(() =>
		overrides.value.filter(
			override => override.status === 'pending' && override.requires_review_from === 'agent'
		)
	);
	const approvedOverrides = computed(() =>
		overrides.value.filter(override => override.status === 'approved')
	);

	const social = computed(() => {
		let sm = core.value?.agent?.social;

		if (!sm) {
			return {};
		}
		if (Array.isArray(sm)) {
			sm = Object.fromEntries(sm.map(s => [s.site?.toLowerCase(), s]));
		}
		return sm;
	});

	const office = computed(() => core.value.office ?? {});

	const agent = computed(() => core.value?.agent ?? {});

	const feeds = computed(() => core.value?.feeds);

	const qlp = computed(() => core.value?.qlp ?? {});

	// HOUSEKEEPING: is this ever used? why is it using core.feeds?
	const meta = computed(() => core.value?.feeds ?? {});

	const preferredPhone = computed(() => {
		const office = core.value?.office ?? {};
		return (
			office.preferred_phone ||
			(office.phone?.business || [])[0] ||
			office.phone?.on_microsite?.phone ||
			null
		);
	});

	const teamMembers = computed(() => {
		const members = core.value?.office?.team?.members ?? [];

		return structuredClone(toRaw(members)).map(m => {
			// image
			if (isObject(m.image)) {
				m.image = m.image.large;
			}
			m.eligible_for_display = m.on_microsite || false;
			return m;
		});
	});

	async function updateConfig(domainName = undefined) {
		if (domainName === undefined) {
			domainName = VueRoute?.params?.domainName;
		}

		const fetcher = useApi(`api/domain/${domainName}/config/?include_pages=false`, {
			message: `The content for ${domainName} did not load properly`,
		});
		abortFunctions.config = { abort: fetcher.abort, canAbort: fetcher.canAbort };

		watch(
			() => fetcher.progress,
			newValue => {
				progress.config = newValue;
			},
			{ immediate: true }
		);

		const { data } = await fetcher.json();
		if (data.value) {
			core.value = data.value.core;
			pages.value = data.value.pages;
			productType.value = data.value.productType;

			abortFunctions.config = {};
			await updateOverrides(domainName);
		}
	}

	async function updateOverrides(domainName) {
		if (domainName === undefined) {
			domainName = VueRoute?.params?.domainName;
		}
		const overridesFetcher = useApi(
			`api/domains/${domainName}/overrides/?status=pending&status=approved`,
			{
				message: `The check for pending content overrides failed.`,
			}
		);

		abortFunctions.overrides = {
			abort: overridesFetcher.abort,
			canAbort: overridesFetcher.canAbort,
		};

		watch(
			() => overridesFetcher.progress,
			newValue => {
				progress.overrides = newValue;
			},
			{ immediate: true }
		);

		const { data: overridesData } = await overridesFetcher.json();
		overrides.value = overridesData.value;

		abortFunctions.overrides = {};
	}

	async function updateEditor(domainName) {
		if (domainName === undefined) {
			domainName = VueRoute?.params?.domainName;
		}

		const fetcher = useApi(`api/domains/${domainName}/content/editor/`, {
			message: `The editor config for ${domainName} did not load properly`,
		});
		abortFunctions.config = { abort: fetcher.abort, canAbort: fetcher.canAbort };

		watch(
			() => fetcher.progress,
			newValue => {
				progress.config = newValue;
			},
			{ immediate: true }
		);

		const { data } = await fetcher.json();
		if (fetcher.error?.value) {
			throw fetcher.error.value;
		}
		editors.value = data.value;
	}

	async function updateEditorVisits() {
		const domainName = VueRoute?.params?.domainName;
		const agentID = VueRoute?.params?.agentID;
		const visits = useLocalStorage('editor_visits', null, {
			serializer: StorageSerializers.object,
			mergeDefaults: true,
		});
		const visitForDomainName = visits.value?.[domainName];
		const lastVisit = visitForDomainName ? parseISO(visitForDomainName) : null;
		const currentVisit = new Date();
		const difference = differenceInHours(currentVisit, lastVisit, {
			roundingMethod: 'round',
		});
		const history = useLocalStorage('editor_history', [], {
			serializer: StorageSerializers.object,
			mergeDefaults: true,
		});

		if (domainName && agentID) {
			history.value = [
				{ domainName, agentID, timestamp: formatISO(currentVisit) },
				...history.value.filter(editor => editor.domainName !== domainName),
			].slice(0, 10);
		}

		if (!lastVisit || difference > 4) {
			await useApi(`api/mxeditor/${domainName}/log_editing/`, {
				message: `An editor api has failed`,
			}).put();
			visits.value = { ...visits.value, [domainName]: formatISO(currentVisit) };
		}
	}

	async function updateUsingDomainContent() {
		const domainName = VueRoute?.params?.domainName;
		const response = await fetch(`${supportStore.host}api/domains/${domainName}/content/`);
		usingDomainContent.value = response.ok;
	}

	function findEditorSection(pageName, nodes) {
		for (let node of nodes) {
			if (node.key === pageName) {
				return node;
			} else if (node.editor_type === 'page' || node.editor_type === 'group') {
				const recursiveResult = findEditorSection(pageName, node.children);
				if (recursiveResult !== null) {
					return recursiveResult;
				}
			}
		}
		return null;
	}

	function editorSection(pageName) {
		return findEditorSection(pageName, editors.value);
	}

	function findEditorsPageComponents(pageName, parentName, nodes) {
		let matches = [];

		for (let node of nodes) {
			if (node.editor_type === 'component') {
				if (pageName === parentName) {
					matches.push(node);
				}
			} else if (node.editor_type === 'page' || node.editor_type === 'group') {
				matches = matches.concat(
					findEditorsPageComponents(pageName, node.key, node.children)
				);
			}
		}

		return matches;
	}

	function getComponentConfig(pageName, editorName) {
		const pageComponents = findEditorsPageComponents(pageName, null, editors.value);
		return pageComponents.find(({ key }) => key === editorName);
	}

	function routeDisabled(pageName) {
		return !editorSection(pageName)?.enabled || false;
	}

	async function initializeEditor() {
		initializing.value = true;
		const domainName = VueRoute?.params?.domainName;
		await this.$reset();

		if (!domainName) {
			return Promise.reject('No domain name available to initialize editor');
		}
		const editorChangesStore = useEditorChanges();

		// updateConfig sets productType & agentID
		await updateUsingDomainContent();
		await Promise.all([
			updateConfig(),
			updateEditorVisits(),
			editorChangesStore.$reset(),
			updateEditor(),
		]);

		initializing.value = false;
		return Promise.resolve(`editor initialized for ${domainName}`);
	}

	async function getArticleBlacklist() {
		const domainName = VueRoute?.params?.domainName;

		const { data } = await useApi(`api/domains/${domainName}/article_blacklist/`, {
			message: `The content for ${domainName} did not load properly`,
		}).json();

		articleBlacklist.value = data.value || [];
		return Promise.resolve();
	}

	async function onReset() {
		await Object.values(abortFunctions).forEach(abortFunction => {
			const { abort = null, canAbort = false } = abortFunction;

			if (canAbort) {
				abort?.();
			}
		});

		progress.config = 0;
		progress.overrides = 0;

		await nextTick();
		return Promise.resolve();
	}
	return {
		core,
		initializing,

		savingChanges,

		overrides,
		pendingOverrides,
		approvedOverrides,
		usingDomainContent,
		suggestions,

		pages,
		editors,
		progress,
		articleBlacklist,
		productType,

		//getters
		social,
		office,
		agent,
		feeds,
		meta,
		preferredPhone,
		teamMembers,
		qlp,
		editorSection,

		// actions
		updateConfig,
		updateEditorVisits,
		updateOverrides,
		updateEditor,
		initializeEditor,
		getArticleBlacklist,
		getComponentConfig,
		routeDisabled,

		// needed for unit tests
		abortFunctions,
		onReset,
	};
});
